import React from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { VILLES, VILLE_SLUGS } from "@/data/ville_content";

/**
 * Bandeau de liens inter-villes pour le maillage SEO interne.
 * Affiche les liens vers les autres villes pour le même métier.
 */
export default function InterVilleLinks({ metierSlug, metierTitle, currentVille }) {
  if (!metierSlug) return null;

  const otherVilles = VILLE_SLUGS.filter((v) => v !== currentVille);

  return (
    <section className="py-10 lg:py-16 bg-slate-50 border-t border-slate-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold text-slate-900">
              {currentVille
                ? `${metierTitle} dans d'autres villes de Suisse romande`
                : `${metierTitle} par ville en Suisse romande`}
            </h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {(currentVille ? otherVilles : VILLE_SLUGS).map((slug) => {
              const ville = VILLES[slug];
              if (!ville) return null;
              return (
                <Link
                  key={slug}
                  to={`/metier/${metierSlug}/${slug}`}
                  data-testid={`interville-link-${slug}`}
                  className="group flex items-center gap-2 bg-white border-2 border-slate-200 rounded-md px-3 py-2.5 hover:border-primary/40 hover:shadow-md transition-all"
                >
                  <img
                    src={ville.image}
                    alt={ville.nom}
                    className="w-8 h-8 rounded object-cover flex-shrink-0"
                    loading="lazy"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-primary truncate">
                      {ville.nom}
                    </p>
                    <p className="text-[0.6rem] text-slate-400">{ville.canton}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Also link back to the generic métier page when on a ville page */}
          {currentVille && (
            <div className="mt-4 text-center">
              <Link
                to={`/metier/${metierSlug}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline"
                data-testid="interville-link-generic"
              >
                Voir {metierTitle} dans toute la Suisse romande
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
